// Core packages
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, Inject, NgZone, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import {
  archiveFileOpensInTab,
  archiveFileShowsInTab,
  revokeArchiveFileUrlOnTabClose,
} from "src/app/modules/properties/document-archive-file-open.utils";
import {
  canDownloadArchiveDocFilesZip,
  fallbackZipFileName,
  skippedFilesCount,
  skippedFilesMessage,
  SKIPPED_FILES_HEADER,
  ZipDownloadMessage,
  zipDownloadErrorMessage,
  zipFileNameFromContentDisposition,
} from "src/app/modules/properties/document-files-zip.utils";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * What the archive page passes in when opening the dialog.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDownloadDialogData {
  /**
   * The row the files belong to.
   *
   * The download is addressed by DOCUMENT ID plus file name, never by path: the back-end resolves the
   * file from the record, so nothing here knows about hosts, folders or which of the two file fields
   * of `data` to read.
   */
  docId: string;
  /** The entries of `data.document[]` of that row */
  files: any[];
  /** `data.barCode` of the row: names the zip of "Scarica tutti" when the response does not */
  barCode?: string;
}

/**
 * One file of the list, as this dialog renders it.
 *
 * @since 3.0.0
 */
interface DownloadableFile {
  filename: string;
  originalName: string;
  /** Bytes, as stored. `0` when the size was not recorded, and then it is not printed. */
  size: number;
  /** True when clicking the name opens the file in a new tab (@see archiveFileOpensInTab) */
  opensInTab: boolean;
  /**
   * True while its own request is in flight, from «Scarica» or from the name: one file downloading
   * must not block the others, and the two ways in to the same file are disabled together
   */
  downloading: boolean;
}

/**
 * Close a tab opened at a click, unless the user already did.
 *
 * @param tab The tab
 */
function closeTab(tab: Window): void {
  if (!tab.closed) {
    tab.close();
  }
}

/**
 * The files attached to one archived document, offered one by one.
 *
 * Same shape as the legacy dialog — a list of files with a download button each, because what the
 * user calls "a document" can be several PDFs — but the download itself is different, and on purpose:
 * the legacy composes a PUBLIC URL out of `file.path` and hands it to the browser, which is where the
 * duplicated-host bug of the attachments comes from. Here every file travels through one
 * authenticated request and is saved from the BLOB, so no path is ever built in the front-end.
 *
 * The name of each file is a link, as on the legacy dialog, and it travels the same authenticated
 * road: the file is asked for to show and opened in a new tab from the Blob (@see onOpen). An `href`
 * could not carry the session headers, and the public address of the legacy link is the road the
 * back-end means to close.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-archive-download-dialog",
  templateUrl: "./document-archive-download-dialog.component.html",
  styleUrls: ["./document-archive-download-dialog.component.scss"],
})
export class DocumentArchiveDownloadDialogComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];

  files: DownloadableFile[] = [];

  /** True when "Scarica tutti" is offered: see `canDownloadArchiveDocFilesZip` */
  canDownloadAll = false;

  /** True while the zip downloads: the button is disabled, so a second click asks for nothing */
  zipDownloading = false;

  /**
   * Class constructor
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentArchiveDownloadDialogData,
    private dialogRef: MatDialogRef<DocumentArchiveDownloadDialogComponent>,
    private documentArchiveDocsService: DocumentArchiveDocsService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private ngZone: NgZone
  ) {
    const files =
      this.data && Array.isArray(this.data.files) ? this.data.files : [];

    // A file with no `filename` cannot be asked for — that field is how the back-end finds it inside
    // the record — so it is left out instead of being offered as a button that fails
    this.files = files
      .filter((file) => file && file.filename)
      .map((file) => {
        const originalName = file.originalName ? file.originalName : file.filename;
        return {
          filename: file.filename,
          originalName,
          size: typeof file.size === "number" ? file.size : 0,
          opensInTab: archiveFileOpensInTab({ mimetype: file.mimetype, originalName }),
          downloading: false,
        };
      });

    // Counted on the list above: the back-end zips the same entries, the ones with a `filename`
    this.canDownloadAll = canDownloadArchiveDocFilesZip(
      this.data ? this.data.docId : null,
      this.files.length
    );
  }

  /**
   * Destroy component
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Size to print next to a file name, in KB. Empty when it was not recorded.
   *
   * @since 3.0.0
   */
  sizeLabel(file: DownloadableFile): string {
    if (!file.size) {
      return "";
    }
    return `(${(file.size / 1024).toFixed(2)} KB)`;
  }

  /**
   * Download one file.
   *
   * The blob is handed to FileSaver as a BLOB and not as a URL, which is what keeps the library from
   * probing the server with a synchronous HEAD request first.
   *
   * The dialog stays open: a document made of four files is downloaded four times, and closing on
   * the first one would be in the way.
   *
   * @since 3.0.0
   */
  onDownload(file: DownloadableFile): void {
    if (!file || file.downloading) {
      return;
    }
    file.downloading = true;

    this.subscriptions.push(
      this.documentArchiveDocsService
        .downloadFile(this.data.docId, file.filename)
        .pipe(finalize(() => (file.downloading = false)))
        .subscribe(
          (blob: any) => {
            // ApiService turns a network failure into an Error emitted as a value
            if (!blob || blob instanceof Error) {
              this.helperService.handleError(blob);
              return;
            }
            FileSaver.saveAs(blob, file.originalName);
          },
          (err: any) => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Open one file in a new tab: the link of its name, as on the legacy dialog.
   *
   * The file travels the road of «Scarica», asked for to show (`disposition=inline`), with the same
   * failures and the same messages:
   *  - a file the browser can show (PDF, PNG, JPEG): the tab is opened AT THE CLICK, empty, and filled
   *    when the file arrives. Opened after the answer it would be a pop-up, and blocked;
   *  - any other file cannot be shown: the click saves it, exactly as «Scarica» does;
   *  - a tab the browser refuses: the file is saved instead, so that the click is never lost.
   *
   * The tab is closed again on a failure, on an answer it cannot show (the file is then saved), and
   * when the dialog is closed while the file is on its way. A tab the user closes while waiting is a
   * cancel: nothing is saved.
   *
   * @since 3.0.0
   */
  onOpen(file: DownloadableFile): void {
    if (!file || file.downloading) {
      return;
    }
    if (!file.opensInTab) {
      this.onDownload(file);
      return;
    }

    const tab = window.open("", "_blank");
    if (!tab) {
      this.onDownload(file);
      return;
    }
    // The tab shows a file of ours: it gets no handle on this page
    tab.opener = null;

    file.downloading = true;
    let shown = false;

    this.subscriptions.push(
      this.documentArchiveDocsService
        .downloadFile(this.data.docId, file.filename, "inline")
        .pipe(
          finalize(() => {
            file.downloading = false;
            // The tab stays open only with the file in it
            if (!shown) {
              closeTab(tab);
            }
          })
        )
        .subscribe(
          (blob: any) => {
            // ApiService turns a network failure into an Error emitted as a value
            if (!blob || blob instanceof Error) {
              this.helperService.handleError(blob);
              return;
            }
            if (tab.closed) {
              return;
            }
            if (!archiveFileShowsInTab(blob)) {
              FileSaver.saveAs(blob, file.originalName);
              return;
            }
            shown = this.showInTab(tab, blob, file.originalName);
          },
          (err: any) => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Download every file of the document as one zip, built by the back-end.
   *
   * Same handling as "Scarica tutti" in the legacy dialog: the zip is saved under the name the
   * back-end sends in `Content-Disposition`, a warning tells how many files it could not include,
   * and the dialog stays open.
   *
   * @since 3.0.0
   */
  onDownloadAll(): void {
    if (!this.canDownloadAll || this.zipDownloading) {
      return;
    }
    this.zipDownloading = true;

    this.subscriptions.push(
      this.documentArchiveDocsService
        .downloadDocFilesZip(this.data.docId)
        .pipe(finalize(() => (this.zipDownloading = false)))
        .subscribe(
          (res: HttpResponse<Blob> | Error) => {
            // ApiService emits a failure without status (network down, connection cut once the
            // zip had started) as a value, not as an error
            if (!(res instanceof HttpResponse) || !(res.body instanceof Blob)) {
              console.warn("Scarica tutti (v3): zip download failed", { status: 0 });
              this.showZipMessage(zipDownloadErrorMessage(0, null, Date.now()), "error");
              return;
            }
            const fileName =
              zipFileNameFromContentDisposition(res.headers.get("Content-Disposition")) ||
              fallbackZipFileName(this.data.barCode);
            FileSaver.saveAs(res.body, fileName);

            const skipped = skippedFilesMessage(
              skippedFilesCount(res.headers.get(SKIPPED_FILES_HEADER))
            );
            if (skipped) {
              this.showZipMessage(skipped, "warning");
            }
          },
          (err: HttpErrorResponse) => {
            const status = err && typeof err.status === "number" ? err.status : 0;
            const retryAfter = err && err.headers ? err.headers.get("Retry-After") : null;
            console.warn("Scarica tutti (v3): zip download failed", { status });
            this.showZipMessage(zipDownloadErrorMessage(status, retryAfter, Date.now()), "error");
          }
        )
    );
  }

  /**
   * Close the dialog.
   *
   * @since 3.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Put a file in the tab opened for it.
   *
   * The address of the file is given back once the tab is closed, checked from outside Angular: the
   * check changes nothing on the page, and inside Angular every tick of it would refresh the whole
   * page under the dialog.
   *
   * @since 3.0.0
   *
   * @returns Whether the tab shows the file. When the tab cannot be pointed at it (taken elsewhere
   *          meanwhile) the file is saved instead, and the tab is left to be closed
   */
  private showInTab(tab: Window, blob: Blob, name: string): boolean {
    const url = URL.createObjectURL(blob);
    try {
      tab.location.href = url;
    } catch (err) {
      URL.revokeObjectURL(url);
      console.warn("Scarica i documenti (v3): the tab could not show the file", err);
      FileSaver.saveAs(blob, name);
      return false;
    }
    this.ngZone.runOutsideAngular(() => revokeArchiveFileUrlOnTabClose(tab, url));
    return true;
  }

  /**
   * Show a translated toast of the zip download
   *
   * @since 3.0.0
   */
  private showZipMessage(message: ZipDownloadMessage, level: "error" | "warning"): void {
    const text = this.translateService.instant(message.key, message.params);
    if (level === "warning") {
      this.toastrService.warning(text);
    } else {
      this.toastrService.error(text);
    }
  }
}
